/**
 * useVoice - Hook for TTS/STT in the tutor interface
 *
 * Phase 1: TTS only (read Sudo's messages aloud)
 * Phase 2: STT for student voice input
 * Phase 3: Streaming STT with real-time transcription
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { api, VoiceStatus } from '../api';

// Chop metadata from server-side dedup
export interface ChopMetadata {
  chopId?: string;
  similarity?: number;
  difference?: string;
}

// Streaming STT callbacks
export interface StreamingSTTCallbacks {
  onInterim?: (text: string) => void;
  onFinal?: (text: string, metadata?: ChopMetadata) => void;
  onError?: (error: string) => void;
}

export function useVoice() {
  const [voiceStatus, setVoiceStatus] = useState<VoiceStatus | null>(null);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [voiceEnabled, setVoiceEnabled] = useState(() => {
    return localStorage.getItem('voiceEnabled') === 'true';
  });
  const isPausedRef = useRef(false); // Ref for audio processor callback

  const audioRef = useRef<HTMLAudioElement | null>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const wsRef = useRef<WebSocket | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const processorRef = useRef<ScriptProcessorNode | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const streamingCallbacksRef = useRef<StreamingSTTCallbacks>({});

  // Check voice service availability on mount
  useEffect(() => {
    api.getVoiceStatus()
      .then(setVoiceStatus)
      .catch(() => setVoiceStatus({ tts: false, stt: false }));
  }, []);

  // Save voice preference
  useEffect(() => {
    localStorage.setItem('voiceEnabled', voiceEnabled.toString());
  }, [voiceEnabled]);

  /**
   * Toggle voice on/off
   */
  const toggleVoice = useCallback(() => {
    setVoiceEnabled(prev => !prev);
  }, []);

  /**
   * Speak text using TTS
   * @param text - Text to speak
   * @param force - Skip voiceEnabled check (for speechMode-controlled components)
   */
  const speak = useCallback(async (text: string, force?: boolean) => {
    if (!voiceStatus?.tts || (!force && !voiceEnabled)) return;

    // Stop any current audio
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }

    setIsSpeaking(true);

    try {
      const audioBuffer = await api.textToSpeech(text);
      if (!audioBuffer) {
        setIsSpeaking(false);
        return;
      }

      // Create blob and audio element
      const blob = new Blob([audioBuffer], { type: 'audio/wav' });
      const url = URL.createObjectURL(blob);
      const audio = new Audio(url);
      audioRef.current = audio;

      audio.onended = () => {
        setIsSpeaking(false);
        URL.revokeObjectURL(url);
        audioRef.current = null;
      };

      audio.onerror = () => {
        setIsSpeaking(false);
        URL.revokeObjectURL(url);
        audioRef.current = null;
      };

      await audio.play();
    } catch (error) {
      console.error('Speak error:', error);
      setIsSpeaking(false);
    }
  }, [voiceStatus, voiceEnabled]);

  /**
   * Stop current speech
   */
  const stopSpeaking = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    setIsSpeaking(false);
  }, []);

  /**
   * Start recording audio for STT
   */
  const startRecording = useCallback(async () => {
    if (!voiceStatus?.stt) return;

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.start();
      setIsRecording(true);
    } catch (error) {
      console.error('Start recording error:', error);
    }
  }, [voiceStatus]);

  /**
   * Stop recording and get transcript
   */
  const stopRecording = useCallback(async (): Promise<string | null> => {
    return new Promise((resolve) => {
      if (!mediaRecorderRef.current || mediaRecorderRef.current.state === 'inactive') {
        setIsRecording(false);
        resolve(null);
        return;
      }

      mediaRecorderRef.current.onstop = async () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        audioChunksRef.current = [];

        // Stop all tracks
        mediaRecorderRef.current?.stream.getTracks().forEach(track => track.stop());
        mediaRecorderRef.current = null;

        setIsRecording(false);

        // Send to STT
        const transcript = await api.speechToText(audioBlob);
        resolve(transcript);
      };

      mediaRecorderRef.current.stop();
    });
  }, []);

  /**
   * Start streaming STT - real-time transcription via WebSocket
   */
  const startStreamingSTT = useCallback(async (callbacks: StreamingSTTCallbacks) => {
    if (!voiceStatus?.stt) return;

    // Close any existing connection first (prevents race conditions when switching modes)
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }

    streamingCallbacksRef.current = callbacks;

    try {
      // Get audio stream
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          channelCount: 1,
          sampleRate: 16000,
          echoCancellation: true,
          noiseSuppression: true,
        },
      });
      streamRef.current = stream;

      // Create WebSocket connection to backend
      // In dev (Vite on 5173/5174), backend runs on 3001. In prod, use same origin.
      const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const wsHost = window.location.hostname;
      const isDev = window.location.port === '5173' || window.location.port === '5174';
      const wsPort = isDev ? '3001' : window.location.port;
      const ws = new WebSocket(`${wsProtocol}//${wsHost}:${wsPort}/ws/stt`);
      wsRef.current = ws;

      ws.onopen = () => {
        console.log('Streaming STT WebSocket connected');
        setIsStreaming(true);

        // Set up audio processing
        const audioContext = new AudioContext({ sampleRate: 16000 });
        audioContextRef.current = audioContext;

        const source = audioContext.createMediaStreamSource(stream);

        // Use ScriptProcessorNode to get raw PCM data
        // Note: ScriptProcessorNode is deprecated but AudioWorklet requires more setup
        const processor = audioContext.createScriptProcessor(4096, 1, 1);
        processorRef.current = processor;

        processor.onaudioprocess = (e) => {
          // Don't send audio when paused
          if (isPausedRef.current) return;

          if (ws.readyState === WebSocket.OPEN) {
            const inputData = e.inputBuffer.getChannelData(0);
            // Convert Float32 to Int16
            const pcmData = new Int16Array(inputData.length);
            for (let i = 0; i < inputData.length; i++) {
              const s = Math.max(-1, Math.min(1, inputData[i]));
              pcmData[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
            }
            ws.send(pcmData.buffer);
          }
        };

        source.connect(processor);
        processor.connect(audioContext.destination);
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data.type === 'interim' && streamingCallbacksRef.current.onInterim) {
            streamingCallbacksRef.current.onInterim(data.text);
          } else if (data.type === 'final' && streamingCallbacksRef.current.onFinal) {
            streamingCallbacksRef.current.onFinal(data.text, { chopId: data.chopId, similarity: data.similarity, difference: data.difference });
          } else if (data.type === 'error' && streamingCallbacksRef.current.onError) {
            streamingCallbacksRef.current.onError(data.message);
          }
        } catch (err) {
          console.error('Failed to parse WebSocket message:', err);
        }
      };

      ws.onerror = (error) => {
        console.error('Streaming STT WebSocket error:', error);
        if (streamingCallbacksRef.current.onError) {
          streamingCallbacksRef.current.onError('WebSocket connection error');
        }
      };

      ws.onclose = () => {
        console.log('Streaming STT WebSocket closed');
        setIsStreaming(false);
      };
    } catch (error) {
      console.error('Failed to start streaming STT:', error);
      if (callbacks.onError) {
        callbacks.onError('Failed to access microphone');
      }
    }
  }, [voiceStatus]);

  /**
   * Pause/resume streaming STT (keeps connection open, just stops sending audio)
   */
  const togglePauseStreaming = useCallback(() => {
    setIsPaused(prev => {
      const newValue = !prev;
      isPausedRef.current = newValue;
      return newValue;
    });
  }, []);

  /**
   * Stop streaming STT
   */
  const stopStreamingSTT = useCallback(() => {
    // Reset pause state when stopping
    setIsPaused(false);
    isPausedRef.current = false;
    // Close WebSocket
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }

    // Stop audio processing
    if (processorRef.current) {
      processorRef.current.disconnect();
      processorRef.current = null;
    }

    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }

    // Stop media stream
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }

    setIsStreaming(false);
    streamingCallbacksRef.current = {};
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopStreamingSTT();
    };
  }, [stopStreamingSTT]);

  return {
    // Status
    ttsAvailable: voiceStatus?.tts ?? false,
    sttAvailable: voiceStatus?.stt ?? false,
    voiceEnabled,
    isSpeaking,
    isRecording,
    isStreaming,
    isPaused,

    // Actions
    toggleVoice,
    speak,
    stopSpeaking,
    startRecording,
    stopRecording,

    // Streaming STT
    startStreamingSTT,
    stopStreamingSTT,
    togglePauseStreaming,
  };
}
